/**
 * Dish Detail Modal (Lightbox) Component
 * Shows large food image, localized full title & description, dietary tags, price, and stepper.
 */

import { getCurrentLanguage, t } from '../../../i18n';
import { renderQtyStepper } from './qty-stepper';
import type { MenuItem } from '../types';

const TAG_INFO: Record<string, { icon: string; label: Record<string, string> }> = {
  veg: {
    icon: '🌿',
    label: { th: 'มังสวิรัติ', en: 'Vegetarian', zh: '素食', ru: 'Вегетарианское', ja: 'ベジタリアン', ko: '채식' },
  },
  spicy: {
    icon: '🌶',
    label: { th: 'รสเผ็ด', en: 'Spicy', zh: '辣味', ru: 'Острое', ja: 'スパイシー', ko: '매운맛' },
  },
  gf: {
    icon: '🌾',
    label: { th: 'ไม่มีกลูเตน', en: 'Gluten-Free', zh: '无麸质', ru: 'Без глютена', ja: 'グルテンフリー', ko: '글루텐 프리' },
  },
};

const SOLD_OUT: Record<string, string> = {
  th: 'หมดแล้ว', en: 'Sold out', zh: '售罄', ru: 'Нет в наличии', ja: '売り切れ', ko: '품절',
};

export function renderDishDetailModal(item: MenuItem, qty: number): string {
  const lang = getCurrentLanguage();
  const name = (item.name as Record<string, string>)[lang] || item.name.en || (item.name as any).th || '';
  const rawDesc = item.description || item.desc;
  const desc = rawDesc ? (typeof rawDesc === 'string' ? rawDesc : (rawDesc as Record<string, string>)[lang] || (rawDesc as any).en || (rawDesc as any).th || '') : '';

  const tagBadges = item.tags.map((tag) => {
    const info = TAG_INFO[tag];
    if (!info) return '';
    const label = info.label[lang] || info.label.en;
    return `
      <span style="
        display:inline-flex;align-items:center;gap:4px;
        padding:4px 8px;border-radius:6px;background:#eef7ee;color:#00B900;
        font-size:12px;font-weight:600;margin-right:6px;margin-bottom:6px;
      ">
        <span>${info.icon}</span>
        <span>${label}</span>
      </span>
    `;
  }).join('');

  const priceStr = `฿${item.price.toLocaleString()}`;

  const stepperHtml = item.available
    ? renderQtyStepper(item.id, qty)
    : `<span style="padding:8px 14px;border-radius:8px;background:#f0f0f0;color:#999;font-size:13px;font-weight:600;">${SOLD_OUT[lang] || SOLD_OUT.en}</span>`;

  return `
    <div
      id="dish-detail-backdrop"
      onclick="if(event.target===this) window.closeDishDetail?.()"
      style="
        position:fixed;inset:0;background:rgba(0,0,0,0.65);
        backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);
        z-index:999;display:flex;align-items:center;justify-content:center;
        padding:16px;animation:fadeIn 0.2s ease-out;
      "
    >
      <div
        role="dialog"
        aria-modal="true"
        style="
          background:#fff;border-radius:20px;max-width:440px;width:100%;
          max-height:90vh;overflow-y:auto;position:relative;
          box-shadow:0 20px 40px rgba(0,0,0,0.25);
          animation:slideUp 0.25s cubic-bezier(0.16,1,0.3,1);
        "
      >
        <button
          onclick="window.closeDishDetail?.()"
          aria-label="Close"
          style="
            position:absolute;top:12px;right:12px;z-index:10;
            width:34px;height:34px;border-radius:50%;
            background:rgba(0,0,0,0.5);color:#fff;border:none;
            font-size:18px;display:flex;align-items:center;justify-content:center;
            cursor:pointer;backdrop-filter:blur(4px);transition:background 0.15s;
          "
        >✕</button>

        ${item.imageUrl ? `
        <div style="width:100%;height:230px;background:#f5f5f5;overflow:hidden;border-top-left-radius:20px;border-top-right-radius:20px;">
          <img
            src="${item.imageUrl}"
            alt="${name}"
            style="width:100%;height:100%;object-fit:cover;display:block;"
          />
        </div>
        ` : `
        <div style="height:24px;"></div>
        `}

        <div style="padding:20px;">
          <div style="font-size:19px;font-weight:700;color:#1a1a1a;margin-bottom:6px;line-height:1.35;">
            ${name}
          </div>

          ${tagBadges ? `<div style="display:flex;flex-wrap:wrap;margin-bottom:10px;">${tagBadges}</div>` : ''}

          <div style="font-size:18px;font-weight:700;color:#00B900;margin-bottom:14px;">
            ${priceStr}
          </div>

          ${desc ? `
          <div style="
            font-size:14px;color:#555;line-height:1.6;margin-bottom:20px;
            padding:12px 14px;background:#f9f9f9;border-radius:10px;border-left:3px solid #00B900;
          ">
            ${desc}
          </div>
          ` : ''}

          <div style="
            display:flex;align-items:center;justify-content:space-between;
            padding-top:14px;border-top:1px solid #eee;
          ">
            <span style="font-size:14px;font-weight:600;color:#333;">${t('yourOrder')}</span>
            <div id="modal-stepper-mount">${stepperHtml}</div>
          </div>
        </div>
      </div>
    </div>
  `;
}
